import { serializeAttachmentUpload } from '@services/attachmentChanges/attachmentUploadOrigins';
import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IDocumentData } from '@models/document/IDocumentData';
import { IFieldChange } from '@models/document/IDocumentHistory';
import { IDocumentResult } from '@models/document/IDocumentResult';
import { IContentModels, IDocumentScheme } from '@models/document/IDocumentScheme';
import { IDocumentStatusBar } from '@models/document/IDocumentStatusBar';
import { IForms } from '@models/Forms/IForms';
import { IFormData } from '@models/IFormData';


export class DocumentService {
    /**
     * @param deferLinks Не ждать сборку таблиц-линков: поля `#links|*` придут заглушками, а строки
     * нужно догрузить getTableLinksData. Включать только там, где эта пара запросов реализована
     * (карточка документа); прочим потребителям нужен полный ответ.
     */
    static async getData(
        docId: string,
        openDocumentGuid: string | undefined = undefined,
        signal?: AbortSignal,
        deferLinks: boolean = false,
        suppressErrorNotification: boolean = false,
    ): Promise<AxiosResponse<IDocumentData>> {
        return $api.get<IDocumentData>(`/document/data/${docId}`, {
            params: {
                openDocumentGuid: openDocumentGuid,
                deferLinks: deferLinks ? true : undefined,
            },
            signal: signal,
            suppressErrorNotification,
        });
    }

    /**
     * Содержимое таблиц-линков (`#links|*`) документа — вторая половина ответа getData:
     * /document/data отдаёт эти поля заглушками, реальные строки грузятся этим запросом
     * параллельно с карточкой (см. useDocumentLinksLoad).
     */
    static async getTableLinksData(
        docId: string,
        openDocumentGuid: string | undefined = undefined,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<IFormData>> {
        return $api.get<IFormData>(`/document/data/${docId}/links`, {
            params: {
                openDocumentGuid: openDocumentGuid,
            },
            signal: signal,
        });
    }

    static async getFormData(docId: string, fields: string[]): Promise<AxiosResponse<IFormData>> {
        return $api.get<IFormData>(`/document/formData/${docId}`, {
            params: {
                fields: fields.join(','),
            },
        });
    }

    static async getExternalDataSourceData(
        docId: string,
        sourceName: string,
    ): Promise<AxiosResponse<IFormData>> {
        return $api.get<IFormData>(`/document/externalDataSource/${docId}/${encodeURIComponent(sourceName)}`);
    }

    static async getDataByAction(
        actId: string,
        docId?: string,
        templateId?: string,
        isFromActivity?: boolean,
        showCurrentValues?: boolean,
        signal?: AbortSignal,
        suppressErrorNotification: boolean = false,
    ): Promise<AxiosResponse<IDocumentData>> {
        const ids = docId?.split(',').filter((id) => id && id !== '-1') ?? [];
        return $api.post<IDocumentData>(`/document/dataByAction/${actId}`, ids, {
            params: {
                templateId: templateId,
                showCurrentValues: showCurrentValues,
                isFromActivity: isFromActivity,
            },
            signal: signal,
            suppressErrorNotification,
        });
    }

    static async getHistory(docId: string, historyHideInvisibleFields: boolean): Promise<AxiosResponse<IFieldChange[]>> {
        return $api.get<IFieldChange[]>(`/document/history/${docId}/${historyHideInvisibleFields}`);
    }

    static async getStatusBar(
        controller: AbortController,
        docId: string,
        openDocumentGuid: string | undefined,
    ): Promise<AxiosResponse<IDocumentStatusBar>> {
        return $api.get<IDocumentStatusBar>(`/document/statusBar/${docId}`, {
            params: {
                openDocumentGuid: openDocumentGuid,
            },
            signal: controller.signal
        });
    }

    static async getScheme(
        docId: string,
        formNameOverride?: string,
        openDocumentGuid: string | undefined = undefined,
        signal?: AbortSignal,
        suppressErrorNotification: boolean = false,
    ): Promise<AxiosResponse<IDocumentScheme>> {
        let url = `/document/scheme/${docId}`;

        return $api.get<IDocumentScheme>(url, {
            params: {
                formNameOverride: formNameOverride,
                openDocumentGuid: openDocumentGuid,
            },
            signal: signal,
            suppressErrorNotification,
        });
    }

    /**
     * Загружает XML контент-модели формы и линкованных потоков.
     * Нужно только для Debug Panel → "Данные формы" — вызывается лениво.
     */
    static async getContentModels(
        docId: string,
        formNameOverride?: string,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<IContentModels>> {
        return $api.get<IContentModels>(`/document/scheme/${docId}/content-models`, {
            params: {
                formNameOverride: formNameOverride,
            },
            signal: signal,
        });
    }
    static async getTableScheme(table: string): Promise<AxiosResponse<IForms>> {
        return $api.get<IForms>(`/document/tableScheme/${table}`);
    }
    static async getSchemeByAction(
        actId: string,
        baseDocId?: string,
        formNameOverride?: string,
        signal?: AbortSignal,
        suppressErrorNotification: boolean = false,
    ): Promise<AxiosResponse<IDocumentScheme>> {
        const ids = baseDocId?.split(',').filter((id) => id && id !== '-1') ?? [];
        return $api.post<IDocumentScheme>(`/document/schemeByAction/${actId}`, ids, {
            params: {
                formNameOverride: formNameOverride,
            },
            signal: signal,
            suppressErrorNotification,
        });
    }

    static async saveData(doc: IDocumentData, templateId?: string): Promise<AxiosResponse<IDocumentResult>> {
        const formData = new FormData();

        if (doc.files) {
            // Бинарники живут только в памяти блока вложений: после восстановления черновика
            // или повторного монтирования строка может остаться без файла. Такую строку `add`
            // сервер всё равно отвергнет («Не найден файл»), поэтому отбрасываем пару целиком.
            // Сопоставление — по `_filename`, как в AttachFilesService.
            const binaries = (doc.files.files ?? []).filter((file): file is File => file instanceof Blob);
            const uploadedNames = new Set(binaries.map((file) => file.name));
            const props = (doc.files.attachProperties ?? []).filter(
                (prop) => prop?._actType !== 'add' || uploadedNames.has(prop?._filename),
            );

            props.forEach((prop) => formData.append('attachProps', serializeAttachmentUpload(prop)));
            binaries.forEach((file) => formData.append('attachFiles', file));
            delete doc.files;
        }

        formData.append('doc', JSON.stringify(doc));
        formData.append('templateId', templateId ?? '');

        return $api.post(`/document/save`, formData, { suppressErrorNotification: true });
    }
}
